import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Logo, CtaButton, MonoLabel } from "../components/kit";
import api, { formatApiErrorDetail } from "../lib/api";
import { MailCheck, MailWarning } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying"); // verifying | ok | error
  const [error, setError] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setError("Verification link missing or malformed.");
      return;
    }
    api.post("/auth/verify-email", { token })
      .then(() => {
        setStatus("ok");
        setTimeout(() => navigate("/app"), 2500);
      })
      .catch((err) => {
        setStatus("error");
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Verification failed.");
      });
  }, [params, navigate]);

  return (
    <div className="min-h-screen dot-grid flex flex-col" data-testid="verify-email-page">
      <div className="max-w-6xl w-full mx-auto px-6 py-6">
        <Link to="/"><Logo className="h-5 sm:h-6" /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-24">
        <div className="w-full max-w-md card-soft p-8 sm:p-10 text-center fade-up">
          {status === "verifying" && (
            <p className="text-[14px] text-[#54505B]" data-testid="verify-email-loading">Verifying…</p>
          )}
          {status === "ok" && (
            <div data-testid="verify-email-success">
              <MailCheck size={36} strokeWidth={1.6} className="mx-auto text-[#28925B]" />
              <MonoLabel>EMAIL VERIFIED</MonoLabel>
              <h1 className="mt-3 text-2xl"><span className="font-serif-it">You're in.</span></h1>
              <p className="mt-3 text-[14px] text-[#54505B]">Your email is confirmed. Taking you to the app…</p>
              <CtaButton className="w-full mt-6" onClick={() => navigate("/app")} data-testid="verify-email-goto-app">Go to the app</CtaButton>
            </div>
          )}
          {status === "error" && (
            <div data-testid="verify-email-error">
              <MailWarning size={36} strokeWidth={1.6} className="mx-auto text-[#B4443C]" />
              <MonoLabel>INVALID LINK</MonoLabel>
              <p className="mt-4 text-[14px] text-[#54505B]">{error}</p>
              <CtaButton className="w-full mt-6" onClick={() => navigate("/app")} data-testid="verify-email-back-btn">Back to the app</CtaButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
